import {
  MIXPANEL_ENGAGEMENT_SESSION_STORAGE_KEY,
  MIXPANEL_FIRST_VISIT_STORAGE_KEY,
  safeReadJson,
  safeRemove,
  safeWriteJson
} from './mixpanelStorage'
import { mixpanelRuntime } from './mixpanelRuntime'
import type { AttributionContext } from './mixpanelTypes'
import type { MixpanelStorage } from './mixpanelStorage'

const FIRST_VISIT_TTL_MS = 90 * 24 * 60 * 60 * 1000
const ENGAGEMENT_SESSION_TTL_MS = 30 * 60 * 1000
const LEGACY_FIRST_VISIT_STORAGE_KEY = 'nordy.mixpanel.first_visit_tracked'

interface FirstVisitMarker {
  savedAt: number
  payload: Record<string, never>
}

interface LegacyFirstVisitMarker {
  createdAt: number
  claimId: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isFirstVisitMarker(value: unknown): value is FirstVisitMarker {
  return (
    isRecord(value) &&
    typeof value.savedAt === 'number' &&
    Number.isFinite(value.savedAt) &&
    isRecord(value.payload) &&
    Object.keys(value.payload).length === 0
  )
}

function isLegacyFirstVisitMarker(
  value: unknown
): value is LegacyFirstVisitMarker {
  return (
    isRecord(value) &&
    typeof value.createdAt === 'number' &&
    Number.isFinite(value.createdAt) &&
    typeof value.claimId === 'string'
  )
}

function getBrowserLocalStorage(): MixpanelStorage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function shouldTrackFirstVisit(
  storage: MixpanelStorage | null = getBrowserLocalStorage(),
  now = Date.now()
): boolean {
  const marker = safeReadJson(
    storage,
    MIXPANEL_FIRST_VISIT_STORAGE_KEY,
    isFirstVisitMarker
  )
  if (marker && now - marker.savedAt < FIRST_VISIT_TTL_MS) {
    safeRemove(storage, LEGACY_FIRST_VISIT_STORAGE_KEY)
    return false
  }
  if (marker) {
    safeRemove(storage, MIXPANEL_FIRST_VISIT_STORAGE_KEY)
    safeRemove(storage, LEGACY_FIRST_VISIT_STORAGE_KEY)
    return true
  }

  const legacyMarker = safeReadJson(
    storage,
    LEGACY_FIRST_VISIT_STORAGE_KEY,
    isLegacyFirstVisitMarker
  )
  if (!legacyMarker || now - legacyMarker.createdAt >= FIRST_VISIT_TTL_MS) {
    safeRemove(storage, LEGACY_FIRST_VISIT_STORAGE_KEY)
    return true
  }

  if (markFirstVisit(storage, legacyMarker.createdAt)) {
    safeRemove(storage, LEGACY_FIRST_VISIT_STORAGE_KEY)
  }
  return false
}

export function markFirstVisit(
  storage: MixpanelStorage | null = getBrowserLocalStorage(),
  now = Date.now()
): boolean {
  return safeWriteJson(storage, MIXPANEL_FIRST_VISIT_STORAGE_KEY, {
    savedAt: now,
    payload: {}
  } satisfies FirstVisitMarker)
}

export function trackFirstVisit(
  context: AttributionContext,
  storage: MixpanelStorage | null = getBrowserLocalStorage(),
  now = Date.now(),
  referrer = document.referrer
): void {
  if (!storage || !shouldTrackFirstVisit(storage, now)) return

  mixpanelRuntime.events.firstVisit({
    landing_page: context.location,
    referrer,
    device_category: context.deviceCategory
  })
  markFirstVisit(storage, now)
}

export interface UserEngagementMetadata {
  engagement_type: 'job_create_attempt'
  $current_url: string
  current_page_title?: string
  run_type: 'comfyui'
  model: string
}

export interface EngagementSession {
  sessionId: string
  expiresAt: number
  engagementSent: boolean
}

interface EngagementAction {
  now: number
  randomUUID: () => string
}

interface EngagementResult {
  session: EngagementSession
  shouldTrack: boolean
}

function isEngagementSession(value: unknown): value is EngagementSession {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false
  }
  const session = value as Record<string, unknown>
  return (
    typeof session.sessionId === 'string' &&
    typeof session.expiresAt === 'number' &&
    typeof session.engagementSent === 'boolean'
  )
}

function createSessionId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export function reduceEngagement(
  currentSession: EngagementSession | null,
  action: EngagementAction
): EngagementResult {
  const session =
    currentSession && currentSession.expiresAt > action.now
      ? currentSession
      : {
          sessionId: action.randomUUID(),
          expiresAt: action.now + ENGAGEMENT_SESSION_TTL_MS,
          engagementSent: false
        }
  const shouldTrack = !session.engagementSent

  return {
    session: {
      ...session,
      expiresAt: action.now + ENGAGEMENT_SESSION_TTL_MS,
      engagementSent: true
    },
    shouldTrack
  }
}

export function toEngagementInsertId(session: EngagementSession): string {
  return `user_engagement:${session.sessionId}`
}

export function trackUserEngagement(payload: UserEngagementMetadata): boolean {
  const storage = getBrowserLocalStorage()
  if (!storage) return false
  const now = Date.now()
  const currentSession = safeReadJson(
    storage,
    MIXPANEL_ENGAGEMENT_SESSION_STORAGE_KEY,
    isEngagementSession
  )
  const result = reduceEngagement(currentSession, {
    now,
    randomUUID: createSessionId
  })
  if (
    !safeWriteJson(
      storage,
      MIXPANEL_ENGAGEMENT_SESSION_STORAGE_KEY,
      result.session
    ) ||
    !result.shouldTrack
  ) {
    return false
  }

  mixpanelRuntime.events.userEngagement({
    ...payload,
    $insert_id: toEngagementInsertId(result.session)
  })
  return true
}
